import React, { useState, useEffect } from 'react';

interface SmoothLoaderProps {
  children: React.ReactNode;
}

const SmoothLoader: React.FC<SmoothLoaderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [contentOpacity, setContentOpacity] = useState(0);
  const [loaderOpacity, setLoaderOpacity] = useState(1);

  // Check for system dark mode preference for initial render
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  // Check if there's a saved theme preference
  const savedTheme = localStorage.getItem('theme');
  // Determine initial theme (saved theme takes precedence over system preference)
  const isDarkMode = savedTheme ? savedTheme === 'dark' : prefersDarkMode;

  // Apply appropriate classes based on theme
  const bgClass = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const textClass = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const borderClass = isDarkMode
    ? 'border-gray-700 border-t-blue-400'
    : 'border-gray-200 border-t-blue-500';
  const pulseClass = isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100';

  useEffect(() => {
    // Start the transition after the component mounts
    const timer = setTimeout(() => {
      // First fade out the loader
      setLoaderOpacity(0);

      // Then after loader fades out, fade in the content
      setTimeout(() => {
        setContentOpacity(1);
        setTimeout(() => {
          // After content is fully visible, remove the loader
          setIsLoading(false);
        }, 400);
      }, 400);
    }, 800); // Give the page some time to load resources

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative min-h-screen ${bgClass}`}>
      {/* The actual content */}
      <div
        className="transition-opacity duration-500 ease-in-out"
        style={{ opacity: contentOpacity }}
      >
        {children}
      </div>

      {/* Loader overlay */}
      {isLoading && (
        <div
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${bgClass} transition-opacity duration-500 ease-in-out`}
          style={{ opacity: loaderOpacity }}
        >
          <div className="relative">
            {/* Pulsing outer circle */}
            <div
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full ${pulseClass} animate-ping opacity-75`}
            ></div>

            {/* Spinning inner circle */}
            <div
              className={`relative w-14 h-14 rounded-full border-4 ${borderClass} animate-spin`}
            ></div>
          </div>

          {/* Loading text */}
          <p className={`mt-4 text-sm ${textClass} font-medium`}>
            Loading portfolio...
          </p>
        </div>
      )}
    </div>
  );
};

export default SmoothLoader;
